import { useTranslation } from "react-i18next";
import { Keyboard, PlayCircle, Languages } from "lucide-react";
import { langMeta, youtubeEmbed } from "@/i18n/languages";

export default function ProductLanguageHelper({ language, tutorial, variant = "product" }) {
  const { t } = useTranslation();
  const code = language || "en";
  if (code === "en") return null;
  const meta = langMeta(code);
  const embed = youtubeEmbed(tutorial?.youtube_url);
  const native = meta.native;
  const typing = meta.typingUrl;
  const isSettings = variant === "settings";
  const howLabel = tutorial?.title || t(isSettings ? "settings.howTo" : "products.howToAdd", { language: native });
  const testid = isSettings ? "settings-lang-helper" : "product-lang-helper";

  return (
    <aside
      data-testid={testid}
      className="sticky top-0 z-30 mb-5 rounded-2xl border-2 border-orange-400 bg-gradient-to-br from-orange-50 via-amber-50 to-white shadow-[0_8px_30px_-12px_rgba(234,88,12,0.45)] overflow-hidden"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500 text-white flex items-center justify-center shrink-0">
            <Languages className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-lg font-extrabold text-slate-900">
              {t(isSettings ? "settings.helperTitle" : "products.helperTitle", { language: native })}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {t(isSettings ? "settings.helperBody" : "products.helperBody", { language: native })}
            </p>
            <p className="text-xs text-slate-500 mt-2">{t(isSettings ? "settings.typeHint" : "products.typeHint")}</p>
          </div>
        </div>
        {embed ? (
          <div className="mt-4 aspect-video w-full rounded-xl overflow-hidden border-2 border-slate-900 bg-slate-900">
            <iframe
              title={howLabel}
              src={embed}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid={isSettings ? "settings-lang-video" : "product-lang-video"}
            />
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-2">
          {embed || tutorial?.youtube_url ? (
            <a
              href={tutorial?.youtube_url || embed}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold"
              data-testid={isSettings ? "settings-lang-howto" : "product-lang-howto"}
            >
              <PlayCircle className="w-4 h-4" /> {t(isSettings ? "settings.watchTutorial" : "products.watchTutorial", { language: native })}
            </a>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-200 text-slate-600 px-4 py-2 text-sm font-semibold" data-testid={isSettings ? "settings-lang-howto-pending" : "product-lang-howto-pending"}>
              <PlayCircle className="w-4 h-4" /> {howLabel}
            </span>
          )}
          {typing ? (
            <a
              href={typing}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-orange-500 text-white px-4 py-2 text-sm font-semibold border-2 border-slate-900"
              data-testid={isSettings ? "settings-lang-typing" : "product-lang-typing"}
            >
              <Keyboard className="w-4 h-4" /> {t("products.openTyping", { language: native })}
            </a>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
